/**
 * @file     useAnalytics.ts
 * @purpose  Analytics hook delegating to IAnalyticsService (currently no-op). Future wiring to real service is a one-line change.
 */

'use client';

import { useMemo } from 'react';
import { createNoopAnalyticsService, createLiveAnalyticsService } from '@/core/analytics';
import { env } from '@/core/config/env';

/**
 * Returns convenience analytics methods for components.
 * Currently backed by a no-op service, all calls are silent.
 * When the real analytics engine is wired in (future L-series task),
 * swap `createNoopAnalyticsService` for the live service singleton.
 */
export const useAnalytics = () => {
  const service = useMemo(() => (env.enableAnalytics && typeof window !== 'undefined' ? createLiveAnalyticsService() : createNoopAnalyticsService()), []);

  // Memoized on the (stable) service so every returned method keeps a stable
  // identity. Fresh identities per render caused an infinite fetch loop in any
  // consumer that listed trackEvent in a useCallback/useEffect dependency
  // (suggested-by-agent page: load -> setState -> render -> new trackEvent -> load).
  return useMemo(() => ({
    trackEvent: service.trackEvent.bind(service),
    trackCtaClick: (label: string, location: string, destination?: string, metadata?: Record<string, unknown>) => {
      service.trackEvent({
        eventName: 'cta_click',
        eventType: 'click',
        category: 'cta',
        label,
        pagePath: location,
        metadata: { destination, ...metadata },
      });
    },
    trackNavClick: (label: string, location: string, destination: string, metadata?: Record<string, unknown>) => {
      service.trackEvent({
        eventName: 'nav_click',
        eventType: 'navigation',
        category: 'navigation',
        label,
        pagePath: location,
        metadata: { destination, ...metadata },
      });
    },
    trackSectionView: (sectionId: string, label?: string, metadata?: Record<string, unknown>) => {
      service.trackSectionView(sectionId, { label, ...metadata });
    },
    /**
     * GA4 standard `form_start` event. Fires once per form, on first user
     * interaction with any field. GA4 expects params: form_id, form_name,
     * form_destination. Additional landing context is passed via metadata.
     */
    trackFormStarted: (formId: string, metadata?: Record<string, unknown>) => {
      service.trackEvent({
        eventName: 'form_start',
        eventType: 'form',
        category: 'form',
        label: formId,
        metadata: { form_id: formId, form_name: formId, ...metadata },
      });
    },
    trackFormFieldInteraction: (formId: string, fieldName: string, metadata?: Record<string, unknown>) => {
      service.trackEvent({
        eventName: 'form_field_interaction',
        eventType: 'form',
        category: 'form',
        label: `${formId}:${fieldName}`,
        metadata,
      });
    },
    trackOutboundLinkClick: (label: string, destination: string, location: string) => {
      service.trackEvent({
        eventName: 'outbound_link_click',
        eventType: 'outbound',
        category: 'outbound',
        label,
        pagePath: location,
        metadata: { destination },
      });
    },
    trackContactMethodClick: (method: 'email' | 'phone', location: string, destination: string) => {
      service.trackEvent({
        eventName: 'contact_method_click',
        eventType: 'contact',
        category: 'contact',
        label: method,
        pagePath: location,
        metadata: { destination },
      });
    },
  }), [service]);
};
